import { isToday, isYesterday, subDays } from 'date-fns';

import { useAiAssistantGetDialogsListQuery } from '@query';

import { MagicAiDialogsListItem } from '@api';

import { useNavLayout } from '@shared/common/components/NavLayout';

import { Typography } from '@ui/uikit/components/common/Typography';
import { ArrowLeft } from '@ui/uikit/components/icons/mono';

interface ChatsForPeriod {
  header: string;
  id: string;
}

interface ChatGroups {
  today: ChatsForPeriod[];
  yesterday: ChatsForPeriod[];
  twoDaysAgo: ChatsForPeriod[];
  older: ChatsForPeriod[];
}

const groupChatsByDate = (chats: MagicAiDialogsListItem[]) => {
  const now = new Date();
  const groups: ChatGroups = {
    today: [],
    yesterday: [],
    twoDaysAgo: [],
    older: [],
  };

  chats.forEach(({ info }) => {
    const chatDate = new Date(info.createdAt);
    const header = info.header;
    const id = info.id;

    if (isToday(chatDate)) {
      if (!groups.today.find((chat) => chat.id === id)) {
        groups.today.push({
          id: id,
          header: header,
        });
      }
    } else if (isYesterday(chatDate)) {
      if (!groups.yesterday.find((chat) => chat.id === id)) {
        groups.yesterday.push({
          id: id,
          header: header,
        });
      }
    } else if (chatDate >= subDays(now, 2)) {
      if (!groups.twoDaysAgo.find((chat) => chat.id === id)) {
        groups.twoDaysAgo.push({
          id: id,
          header: header,
        });
      }
    } else {
      if (!groups.older.find((chat) => chat.id === id)) {
        groups.older.push({
          id: id,
          header: header,
        });
      }
    }
  });

  return groups;
};

type HistoryProps = {
  backToChat: () => void;
};

export const HistoryScreen = ({ backToChat }: HistoryProps) => {
  const { user } = useNavLayout();

  const { data, isPending, isLoading } = useAiAssistantGetDialogsListQuery({ enabled: !!user });

  const handleChatHistoryItemClick = (id: string) => {
    backToChat();
    localStorage.setItem('currentSessionChatId', id);
  };

  const groupedChats = data
    ? groupChatsByDate(data)
    : {
        today: [],
        yesterday: [],
        twoDaysAgo: [],
        older: [],
      };

  if (isPending || isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <h2 className="text-h2-a">Loading...</h2>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col h-full">
      <div className="p-3 border-b border-base-border-light">
        <div className="flex items-center cursor-pointer gap-4" onClick={backToChat}>
          <ArrowLeft className="icon-xs" />
        </div>
      </div>
      <div className="flex flex-col w-full max-h-full p-3 overflow-y-auto text-body-sm-a">
        {!!groupedChats.today.length && (
          <>
            <div className="p-3">
              <Typography variant="caption-xs" color="tertiary">
                Today
              </Typography>
            </div>
            {groupedChats.today.map((chat) => {
              return (
                <div
                  className="p-3 cursor-pointer hover:bg-base-bg-secondary hover:rounded-lg "
                  onClick={() => handleChatHistoryItemClick(chat.id)}
                >
                  {chat.header || 'New Chat'}
                </div>
              );
            })}
          </>
        )}
        {!!groupedChats.yesterday.length && (
          <>
            <div className="p-3">
              <Typography variant="caption-xs" color="tertiary">
                Yesterday
              </Typography>
            </div>
            {groupedChats.yesterday.map((chat) => {
              return (
                <div
                  className="p-3 cursor-pointer hover:bg-base-bg-secondary hover:rounded-lg"
                  onClick={() => handleChatHistoryItemClick(chat.id)}
                >
                  {chat.header || 'New Chat'}
                </div>
              );
            })}
          </>
        )}
        {!!groupedChats.twoDaysAgo.length && (
          <>
            <div className="p-3">
              <Typography variant="caption-xs" color="tertiary">
                Two Days Ago
              </Typography>
            </div>
            {groupedChats.twoDaysAgo.map((chat) => {
              return (
                <div
                  className="p-3 cursor-pointer hover:bg-base-bg-secondary hover:rounded-lg"
                  onClick={() => handleChatHistoryItemClick(chat.id)}
                >
                  {chat.header || 'New Chat'}
                </div>
              );
            })}
          </>
        )}
        {!!groupedChats.older.length && (
          <>
            <div className="p-3">
              <Typography variant="caption-xs" color="tertiary">
                Older
              </Typography>
            </div>
            {groupedChats.older.map((chat) => {
              return (
                <div
                  className="p-3 cursor-pointer hover:bg-base-bg-secondary hover:rounded-lg"
                  onClick={() => handleChatHistoryItemClick(chat.id)}
                >
                  {chat.header || 'New Chat'}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
